<template>
  <b-row class="courier-documentation">
    <b-col>
      <b-row class="mb-3">
        <b-col>
          <h5 v-translate>Courier Documentation</h5>
          <b-form @submit.prevent="saveDocumentation">

            <b-form-group>
              <label for="card-id-number" v-translate>Document Number</label>
              <b-form-input id="card-id-number" v-model="form.card_id_number" type="text" required />
            </b-form-group>

            <b-form-group>
              <label for="drivers-license-expiration" v-translate>Driver's License Exp. Date</label>
              <b-form-input id="drivers-license-expiration" v-model="form.drivers_license_expiration" type="date" />
            </b-form-group>

            <b-form-group v-if="withTerms">
              <label for="accepted-terms" v-translate>Accepted Courier Terms of Use</label>
              <b-form-input id="accepted-terms" :value="user.accepted_terms" type="text" readonly />
              <small v-if="currentTerms" :class="user.accepted_terms === currentTerms ? 'text-success' : 'text-danger'" v-translate="{currentTerms}">Current Terms: %{currentTerms}</small>
            </b-form-group>

            <b-form-group class="form-actions">
              <b-button size="md" :variant="hasChanges ? 'secondary' : 'dark'" :disabled="!hasChanges || saving" type="submit">
                <b-spinner small v-if="saving"></b-spinner>
                <translate>Save Changes</translate>
              </b-button>
            </b-form-group>

          </b-form>
        </b-col>
      </b-row>
      <slot />
    </b-col>
    <b-col>
      <b-row>
        <b-col cols="12" class="text-center">
          <h5 v-translate>Documentation Pictures</h5>
        </b-col>
        <b-col v-for="picture in pictureFields" :key="picture.key" cols="12" lg="6" class="mb-3 text-center">
          <h6>{{ picture.label }}</h6>
          <div class="courier-documentation__picture">
            <template v-if="form[picture.key]">
              <b-img rounded="rounded" :src="form[picture.key]" style="width: auto; height: auto; max-width: 100px; max-height: 100px;" blank-color="#777" alt="img" class="m-1" /><br>
              <div>
                <b-button size="sm" @click.prevent="openUploader(picture.key)">
                  <translate>Change</translate>
                </b-button>
                <b-button size="sm" variant="danger" @click.stop="deletePicture(picture.key)">
                  <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </div>
            </template>
            <b-button v-else size="sm" @click.prevent="openUploader(picture.key)" v-translate>Upload Image</b-button>
          </div>
        </b-col>
      </b-row>
    </b-col>

    <b-modal ref="modalUploadDocumentImage" hide-footer>
      <template #modal-title>
        <translate>Add a new document image</translate>
      </template>
      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
      <profile-picture-uploader @updateProfileImage="savePicture" />
    </b-modal>
  </b-row>
</template>

<script>
import { UserService } from '@/services/api'
import ProfilePictureUploader from '@/components/ProfilePictureUploader'

const TEXT_FIELDS = ['card_id_number', 'drivers_license_expiration']
const PICTURE_FIELDS = ['card_id_front_picture', 'card_id_back_picture', 'drivers_license_front_picture', 'drivers_license_back_picture']

export default {
  name: 'CourierDocumentation',
  components: {
    ProfilePictureUploader
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    // Accepted terms are only shown on the edit page, read-only: the courier accepts them and the company cannot edit them
    withTerms: {
      type: Boolean,
      default: false
    },
    // Values typed elsewhere that could not be saved yet; used only where the user has none
    prefill: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {},
      saving: false,
      uploadingField: null
    }
  },
  computed: {
    pictureFields() {
      return [
        { key: 'card_id_front_picture', label: this.$gettext('Document Front') },
        { key: 'card_id_back_picture', label: this.$gettext('Document Back') },
        { key: 'drivers_license_front_picture', label: this.$gettext('Driver\'s License Front') },
        { key: 'drivers_license_back_picture', label: this.$gettext('Driver\'s License Back') }
      ]
    },
    changedFields() {
      return TEXT_FIELDS.filter(x => (this.form[x] || null) !== (this.user[x] || null))
    },
    hasChanges() {
      return this.changedFields.length > 0
    },
    currentTerms() {
      const country = (this.$countries || []).find(x => x.iso_code === this.user.country_iso_code)
      if (!country) return null
      return this.user.company_id ? country.companies_terms : country.individual_terms
    }
  },
  watch: {
    user: {
      handler: 'resetForm',
      immediate: true
    }
  },
  methods: {
    resetForm() {
      this.form = [...TEXT_FIELDS, ...PICTURE_FIELDS].reduce((form, key) => ({ ...form, [key]: this.user[key] || this.prefill[key] || null }), {})
    },
    saveDocumentation() {
      const data = this.changedFields.reduce((data, key) => ({ ...data, [key]: this.form[key] || null }), {})
      this.saving = true
      UserService.update(this.user.id, data)
        .then(() => {
          this.showUpdateSuccess()
          this.$emit('updated')
        }, () => {})
        .finally(() => {
          this.saving = false
        })
    },
    openUploader(field) {
      this.uploadingField = field
      this.$refs.modalUploadDocumentImage.show()
    },
    // Pictures are saved as soon as they are uploaded or deleted, without touching the text fields
    savePicture(url) {
      const field = this.uploadingField
      UserService.update(this.user.id, { [field]: url })
        .then(() => {
          this.form[field] = url
          this.uploadingField = null
          this.$refs.modalUploadDocumentImage.hide()
          this.showUpdateSuccess()
        }, () => {})
    },
    deletePicture(field) {
      if (!confirm(this.$gettext('Are you sure you want to DELETE this document picture?'))) return
      UserService.update(this.user.id, { [field]: null })
        .then(() => {
          this.form[field] = null
          this.showUpdateSuccess()
        }, () => {})
    },
    showUpdateSuccess() {
      this.makeToast(this.$gettext('Success'), this.$gettext('The User has been updated! 👍'), 'success', false, 4000)
    }
  }
}
</script>
<style>
.courier-documentation__picture {
  width: 150px;
  height: 150px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
